import { transformationTooltips, transportationLabels } from '../../../../../../consts/conditions';
import { PermissionGuard } from '../../../../../../components/PermissionGuard';

import { ActionIcon, Box, Flex, Text, Menu, rem, Button, Checkbox } from '@mantine/core';
import { FaPlus } from 'react-icons/fa6';
import { TbFilePencil, TbPencil } from 'react-icons/tb';
import { HiOutlineTrash } from 'react-icons/hi';
import { MdDragIndicator } from 'react-icons/md';
import { TbRowInsertBottom } from 'react-icons/tb';
import { TbRowInsertTop } from 'react-icons/tb';
import { TbDotsVertical } from 'react-icons/tb';
import { FaRegClone } from 'react-icons/fa';
import { useMemo, useState } from 'react';
import { LuRotateCw } from 'react-icons/lu';
import { useReRunAiActions } from '../../../../../../services/period.service';
import { toast } from 'react-hot-toast';
import { extractApiError } from '../../../../../../utils';

const LETTERS_LENGTH = 60;

function hasTransformAI(data) {
  return data.some(item => item.thenInputType === 'ai_input' && item.then?.ai_input?.input?.trim());
}

export default function TransformationItem({
  transform,
  setAmongIndex,
  onDelete,
  handleEditTransformation,
  toggleStepIn,
  stepIn,
  idx,
  onClear,
  dragProvided,
  panelWidth,
  onEditNote,
  showReRunAiActions,
  transformationId,
  disableMenu,
  isSelectable,
  isSelected,
  onToggle,
  addTransformation,
  canManageWorkflow = true
}) {
  const [isExpandedText, setIsExpandedText] = useState(false);
  const [isExpandedError, setIsExpandedError] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const onMouseEnter = () => {
    setIsHovering(true);
  };

  const onMouseLeave = () => {
    setIsHovering(false);
  };

  const reRunActionMutation = useReRunAiActions();
  const hasAiInput = useMemo(() => hasTransformAI(transform?.items || []), [transform.items]);

  const calculatedLength = useMemo(() => {
    // Calculate base length based on panel width
    const baseLength = Math.floor(panelWidth / 12);

    // Add padding for additional UI elements
    const paddedLength = baseLength + 16;

    // Ensure minimum length for readability
    const minLength = Math.max(paddedLength, LETTERS_LENGTH);

    // Cap maximum length to prevent excessive text
    const maxLength = Math.min(minLength, 120);

    return maxLength;
  }, [panelWidth]);

  const note = useMemo(() => {
    // If both are null/empty, fall back to default tooltip
    return transform.note || transform.auto_note || transformationTooltips[transform.name];
  }, [transform]);

  const actionText = useMemo(() => {
    const value = note;

    if (isExpandedText || value?.length <= calculatedLength) return value;

    return note?.slice(0, calculatedLength);
  }, [note, isExpandedText, calculatedLength]);

  const warningMessage =
    'You need to solve this issue; otherwise, the next action will not be applied.';

  // Get first warning from items
  const warningFromItems = useMemo(() => {
    return transform?.items?.find(item => item.warning)?.warning || null;
  }, [transform.items]);

  const errorText = useMemo(() => {
    const value = transform.error;

    if (isExpandedError || value?.length <= calculatedLength) return value;

    return value?.slice(0, calculatedLength);
  }, [transform.error, isExpandedError, calculatedLength]);

  const warningText = useMemo(() => {
    const value = warningFromItems;

    if (isExpandedError || value?.length <= calculatedLength) return value;

    return value?.slice(0, calculatedLength);
  }, [warningFromItems, isExpandedError, calculatedLength]);

  const textMarginLeft = useMemo(() => {
    return idx > 8 ? rem(!disableMenu ? 42 : 7) : rem(!disableMenu ? 35 : 0);
  }, [idx, disableMenu, isSelectable]);

  const onSelectDirection = dir => {
    setAmongIndex(prev => {
      if (prev === idx) onClear();

      if (dir === 'above') {
        return idx - 1;
      }

      return prev === idx ? null : idx;
    });
  };

  const onToggleText = e => {
    e.preventDefault();
    e.stopPropagation();

    setIsExpandedText(prev => !prev);
  };

  const onToggleError = e => {
    e.preventDefault();
    e.stopPropagation();

    setIsExpandedError(prev => !prev);
  };

  const renderText = (fullText, isExpanded) => {
    return fullText?.length > calculatedLength && !isExpanded && '..';
  };

  const onEditTransformation = e => {
    e.preventDefault();
    e.stopPropagation();

    handleEditTransformation(idx, transform.items);
  };

  const handleInsertAbove = e => {
    e.stopPropagation();
    onSelectDirection('above');
  };

  const handleInsertBelow = e => {
    e.stopPropagation();
    onSelectDirection('below');
  };

  const handleReRunAiActions = e => {
    e.stopPropagation();

    if (!canManageWorkflow) return;

    const aiInput = transform.items.find(item => item.thenInputType === 'ai_input')?.then?.ai_input;

    reRunActionMutation.mutate(
      {
        transformationId: transformationId,
        stepId: transform.id,
        data: aiInput
      },
      {
        onSuccess: () => {
          toast.success('Action re-run successfully');
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to re-run this transformation action.'));
        }
      }
    );
  };

  const handleToggle = e => {
    e.preventDefault();
    e.stopPropagation();
    onToggle();
  };

  const onDuplicateActionClick = (idx, transformItems) => {
    if (Array.isArray(transformItems) && transformItems.length) {
      addTransformation(
        transformItems.map(item => ({ ...item, systemId: undefined, isNew: true })),
        {
          onSuccess: () => {},
          onError: err => {
            toast.error(extractApiError(err, 'Unable to duplicate this transformation action.'));
          }
        }
      );
    }
  };

  const isActive = stepIn === idx + 1;

  return (
    <Box
      id={`grid-table-transformation-item-shell-${idx}`}
      w="100%"
      py={rem(4)}
      sx={() => ({
        borderTop: idx > 0 && '1px solid #E9ECEF'
      })}
    >
      <Box
        p={rem(4)}
        sx={theme => ({
          border: '1px solid',
          borderColor: isActive
            ? transform.error
              ? theme.colors.red[6]
              : warningFromItems
                ? theme.colors.orange[6]
                : theme.colors.blue[6]
            : '#fff',
          borderRadius: '8px',
          cursor: 'pointer'
        })}
        w="100%"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        bg={isActive ? (transform.error ? 'red.0' : warningFromItems ? 'orange.0' : 'blue.0') : ''}
        onClick={() => toggleStepIn(idx + 1)}
        id={isActive ? 'active_transformation-item' : `${idx}-transformation-item`}
      >
        <Flex align="center" justify="space-between">
          <Flex align="center" gap="4px">
            {!disableMenu && !isSelectable && (
              <ActionIcon
                id={`grid-table-transformation-item-drag-${idx}`}
                {...dragProvided.dragHandleProps}
                size="xs"
                variant="transparent"
                c={transform.error ? 'red.6' : '#ADB5BD'}
              >
                <MdDragIndicator />
              </ActionIcon>
            )}

            {isSelectable && (
              <Checkbox
                id={`grid-table-transformation-item-select-${idx}`}
                mr={rem(2)}
                checked={isSelected}
                onClick={handleToggle}
                size="xs"
                variant="transparent"
                styles={{
                  input: {
                    cursor: 'pointer'
                  }
                }}
              />
            )}

            <Text
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
              c={transform.error ? 'red.6' : warningFromItems ? 'orange.6' : 'black'}
              fz="12px"
              lh="17px"
            >
              {idx + 1}. {transportationLabels[transform.name]}{' '}
              {hasAiInput && (
                <Box
                  w="14px"
                  h="14px"
                  bg="#228BE6"
                  sx={() => ({ borderRadius: 4, justifyContent: 'center', alignItems: 'center' })}
                  c="white"
                  fz="10px"
                  lh="14px"
                  fw={600}
                  display="flex"
                >
                  AI
                </Box>
              )}
            </Text>
          </Flex>

          <Flex align="center" className="actions" gap={rem(8)} h={rem(16)}>
            {(isActive || isHovering) && !disableMenu && (
              <Menu shadow="md" width={160}>
                <Menu.Target>
                  <ActionIcon
                    id={`grid-table-transformation-item-insert-${idx}`}
                    width={rem(16)}
                    height={rem(16)}
                    size="xs"
                    variant="transparent"
                    data-tooltip-id="among-add-btn"
                    disabled={!canManageWorkflow}
                    onClick={e => e.stopPropagation()}
                  >
                    <FaPlus color="#ADB5BD" size={16} />
                  </ActionIcon>
                </Menu.Target>

                <PermissionGuard allowed={canManageWorkflow}>
                  <ActionIcon
                    id={`grid-table-transformation-item-edit-${idx}`}
                    width={rem(16)}
                    height={rem(16)}
                    size="xs"
                    variant="transparent"
                    onClick={onEditTransformation}
                  >
                    <TbPencil color="#ADB5BD" size={16} />
                  </ActionIcon>
                </PermissionGuard>

                <Menu.Dropdown p="12">
                  <Menu.Item
                    id={`grid-table-transformation-item-insert-above-${idx}`}
                    leftSection={<TbRowInsertBottom color="#ADB5BD" fontSize="16px" />}
                    onClick={handleInsertAbove}
                  >
                    Insert Above
                  </Menu.Item>
                  <Menu.Item
                    id={`grid-table-transformation-item-insert-below-${idx}`}
                    leftSection={<TbRowInsertTop color="#ADB5BD" fontSize="16px" />}
                    onClick={handleInsertBelow}
                  >
                    Insert Below
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}

            {(isActive || isHovering) && !disableMenu && (
              <Menu shadow="md" closeOnClickOutside>
                <Menu.Target>
                  <ActionIcon
                    id={`grid-table-transformation-item-menu-${idx}`}
                    width={rem(16)}
                    height={rem(16)}
                    size="xs"
                    variant="transparent"
                    data-tooltip-id="among-add-btn"
                    data-testid="dots-menu-button"
                    disabled={!canManageWorkflow}
                    onClick={e => e.stopPropagation()}
                  >
                    <TbDotsVertical color="#ADB5BD" size={16} />
                  </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown p="12">
                  {showReRunAiActions && (
                    <Menu.Item
                      id={`grid-table-transformation-item-re-run-${idx}`}
                      leftSection={
                        <LuRotateCw
                          className={reRunActionMutation.isLoading && 'custom-spinner'}
                          color="#ADB5BD"
                          fontSize="16px"
                        />
                      }
                      c="black"
                      onClick={handleReRunAiActions}
                      loading={reRunActionMutation.isLoading}
                      disabled={reRunActionMutation.isLoading || !canManageWorkflow}
                      closeMenuOnClick={false}
                    >
                      Re-Run this Action
                    </Menu.Item>
                  )}

                  <Menu.Item
                    id={`grid-table-transformation-item-note-${idx}`}
                    leftSection={<TbFilePencil color="#ADB5BD" fontSize="16px" />}
                    c="black"
                    onClick={onEditNote}
                  >
                    Edit Note
                  </Menu.Item>
                  <Menu.Item
                    id={`grid-table-transformation-item-duplicate-${idx}`}
                    leftSection={<FaRegClone color="#ADB5BD" fontSize="14px" />}
                    onClick={() => onDuplicateActionClick(idx, transform.items)}
                    data-testid="duplicate-action-menu-item"
                  >
                    Duplicate Action
                  </Menu.Item>
                  <Menu.Item
                    id={`grid-table-transformation-item-delete-${idx}`}
                    leftSection={<HiOutlineTrash color="#FF6B6B" fontSize="16px" />}
                    c="red.5"
                    onClick={() => onDelete(idx)}
                  >
                    Delete Action
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Flex>
        </Flex>
        {!transform.error && warningFromItems && (
          <>
            <Text ml={textMarginLeft} mt={rem(4)} c="orange.6" fz="12px" lh="17px" fw={400}>
              {warningText}
              {renderText(warningFromItems, isExpandedError)}
              {warningFromItems?.length > calculatedLength && (
                <Button
                  id={`grid-table-transformation-item-expand-warning-${idx}`}
                  size="xs"
                  variant="transparent"
                  onClick={onToggleError}
                  c="#228BE6"
                  fw={400}
                  td="underline"
                  h={14}
                  p={0}
                  sx={{ display: 'inline', verticalAlign: 'text-bottom', marginLeft: '4px' }}
                >
                  {isExpandedError ? 'See less' : 'See more'}
                </Button>
              )}
            </Text>
          </>
        )}

        {!transform.error && (
          <Text ml={textMarginLeft} mt={rem(2)} c="dark.2" fz="12px" lh="17px" fw={400}>
            {actionText}
            {renderText(note, isExpandedText)}
            {note?.length > calculatedLength && (
              <Button
                id={`grid-table-transformation-item-expand-note-${idx}`}
                size="xs"
                variant="transparent"
                onClick={onToggleText}
                c="#228BE6"
                fw={400}
                td="underline"
                h={14}
                p={0}
                sx={{ display: 'inline', verticalAlign: 'text-bottom', marginLeft: '4px' }}
              >
                {isExpandedText ? 'See less' : 'See more'}
              </Button>
            )}
          </Text>
        )}
        {transform.error && (
          <>
            <Text ml={textMarginLeft} mt={rem(4)} c="red.6" fz="12px" lh="17px" fw={400}>
              {warningMessage}
            </Text>
            <Text ml={textMarginLeft} mt={rem(4)} c="gray.6" fz="12px" lh="17px" fw={400}>
              {errorText}
              {renderText(transform.error, isExpandedError)}
              {transform.error?.length > calculatedLength && (
                <Button
                  id={`grid-table-transformation-item-expand-error-${idx}`}
                  size="xs"
                  variant="transparent"
                  onClick={onToggleError}
                  c="#228BE6"
                  fw={400}
                  td="underline"
                  h={14}
                  p={0}
                  sx={{ display: 'inline', verticalAlign: 'text-bottom', marginLeft: '4px' }}
                >
                  {isExpandedError ? 'See less' : 'See more'}
                </Button>
              )}
            </Text>
          </>
        )}
      </Box>
    </Box>
  );
}
